// Cross-platform feedback: haptics, sounds and local notifications.
// Works in the browser (Web Vibration / Notifications APIs + WebAudio) and on
// Capacitor native (forwards to @capacitor/haptics + @capacitor/local-notifications).

import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { LocalNotifications } from "@capacitor/local-notifications";
import { t } from "./i18n";

// ---------- Haptics ----------
export const haptic = {
  light: () => Haptics.impact({ style: ImpactStyle.Light }).catch(() => {}),
  medium: () => Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {}),
  heavy: () => Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {}),
  success: () => Haptics.notification({ type: NotificationType.Success }).catch(() => {}),
  warning: () => Haptics.notification({ type: NotificationType.Warning }).catch(() => {}),
  error: () => Haptics.notification({ type: NotificationType.Error }).catch(() => {}),
};

// ---------- Sounds (WebAudio — no external assets) ----------
let _ctx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!_ctx) _ctx = new Ctx();
  return _ctx;
};

let _muted = false;
try {
  _muted = localStorage.getItem("rami_sound_muted") === "1";
} catch {}

export const isMuted = () => _muted;
export const setMuted = (m) => {
  _muted = !!m;
  try { localStorage.setItem("rami_sound_muted", _muted ? "1" : "0"); } catch {}
};

// The first AudioContext costs hundreds of ms (the browser opens the audio
// output: ~500 ms measured in Chrome). Made by the first sound, it froze the
// screen right then — the end of a round, when no sound had played before
// (pose15: the summary came half a second late). It is made instead when the
// page is idle after the player's first tap, which also lets it start.
if (typeof window !== "undefined") {
  const warmUp = () => {
    window.removeEventListener("pointerdown", warmUp, true);
    window.removeEventListener("keydown", warmUp, true);
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 300));
    idle(() => {
      if (_muted) return;
      const ctx = getCtx();
      if (ctx?.state === "suspended") ctx.resume().catch(() => {});
    }, { timeout: 2000 });
  };
  window.addEventListener("pointerdown", warmUp, true);
  window.addEventListener("keydown", warmUp, true);
}

const playTone = (notes) => {
  if (_muted) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const now = ctx.currentTime;
  notes.forEach(({ freq, start, dur, type = "sine", gain = 0.18 }) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.value = 0;
    g.gain.linearRampToValueAtTime(gain, now + start + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(now + start);
    osc.stop(now + start + dur + 0.02);
  });
};

export const sound = {
  // Generic card whoosh (e.g. fly animations)
  cardFly: () => {
    if (_muted) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const dur = 0.32;
    const bufLen = Math.ceil(ctx.sampleRate * (dur + 0.06));
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bpf = ctx.createBiquadFilter();
    bpf.type = "bandpass";
    bpf.frequency.setValueAtTime(3000, now);
    bpf.frequency.exponentialRampToValueAtTime(700, now + dur);
    bpf.Q.value = 2.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.18, now + 0.010);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(bpf).connect(g).connect(ctx.destination);
    src.start(now);
    src.stop(now + dur + 0.05);
  },

  // Draw: crisp card-slide from deck with resonant pop
  draw: () => {
    if (_muted) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;

    // High-pass sweep — card sliding off deck
    const slideDur = 0.26;
    const bufLen = Math.ceil(ctx.sampleRate * (slideDur + 0.04));
    const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hpf = ctx.createBiquadFilter();
    hpf.type = "highpass";
    hpf.frequency.setValueAtTime(4200, now);
    hpf.frequency.exponentialRampToValueAtTime(1400, now + slideDur);
    hpf.Q.value = 1.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(0.22, now + 0.007);
    g.gain.exponentialRampToValueAtTime(0.0001, now + slideDur);
    src.connect(hpf).connect(g).connect(ctx.destination);
    src.start(now);
    src.stop(now + slideDur + 0.03);

    // Resonant "pop" — card leaving the deck
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(310, now);
    osc.frequency.exponentialRampToValueAtTime(140, now + 0.07);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, now);
    og.gain.linearRampToValueAtTime(0.17, now + 0.004);
    og.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
    osc.connect(og).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.10);
  },

  // Discard: satisfying card-slap — sharp click + low thud
  discard: () => {
    if (_muted) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const now = ctx.currentTime;

    // Sharp click transient (card surface hitting table)
    const cBufLen = Math.ceil(ctx.sampleRate * 0.004);
    const cBuf = ctx.createBuffer(1, cBufLen, ctx.sampleRate);
    const cD = cBuf.getChannelData(0);
    for (let i = 0; i < cBufLen; i++) cD[i] = Math.random() * 2 - 1;
    const cSrc = ctx.createBufferSource();
    cSrc.buffer = cBuf;
    const cG = ctx.createGain();
    cG.gain.setValueAtTime(0.40, now);
    cG.gain.exponentialRampToValueAtTime(0.0001, now + 0.028);
    cSrc.connect(cG).connect(ctx.destination);
    cSrc.start(now);
    cSrc.stop(now + 0.032);

    // Low body thud (card mass)
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(170, now);
    osc.frequency.exponentialRampToValueAtTime(72, now + 0.13);
    const og = ctx.createGain();
    og.gain.setValueAtTime(0.0001, now);
    og.gain.linearRampToValueAtTime(0.26, now + 0.006);
    og.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
    osc.connect(og).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.17);

    // Air displacement puff (card pressing air)
    const pBufLen = Math.ceil(ctx.sampleRate * 0.07);
    const pBuf = ctx.createBuffer(1, pBufLen, ctx.sampleRate);
    const pD = pBuf.getChannelData(0);
    for (let i = 0; i < pBufLen; i++) pD[i] = Math.random() * 2 - 1;
    const pSrc = ctx.createBufferSource();
    pSrc.buffer = pBuf;
    const bpf = ctx.createBiquadFilter();
    bpf.type = "bandpass";
    bpf.frequency.setValueAtTime(900, now);
    bpf.Q.value = 2.8;
    const pG = ctx.createGain();
    pG.gain.setValueAtTime(0.0001, now);
    pG.gain.linearRampToValueAtTime(0.09, now + 0.004);
    pG.gain.exponentialRampToValueAtTime(0.0001, now + 0.07);
    pSrc.connect(bpf).connect(pG).connect(ctx.destination);
    pSrc.start(now);
    pSrc.stop(now + 0.08);
  },
  combo: () => playTone([
    { freq: 660, start: 0, dur: 0.10, type: "triangle", gain: 0.14 },
    { freq: 880, start: 0.10, dur: 0.14, type: "triangle", gain: 0.14 },
  ]),
  error: () => playTone([
    { freq: 220, start: 0, dur: 0.08, type: "sawtooth", gain: 0.10 },
    { freq: 150, start: 0.08, dur: 0.16, type: "sawtooth", gain: 0.10 },
  ]),
  yourTurn: () => playTone([
    { freq: 880, start: 0,    dur: 0.10, type: "triangle", gain: 0.14 },
    { freq: 1320, start: 0.12, dur: 0.14, type: "triangle", gain: 0.14 },
  ]),
  // The turn timer, on my own clock (Game.jsx): 10 seconds left, then a soft tick for each of the last 5.
  timeHurry: () => playTone([
    { freq: 988, start: 0,    dur: 0.09, type: "triangle", gain: 0.12 },
    { freq: 988, start: 0.16, dur: 0.09, type: "triangle", gain: 0.12 },
  ]),
  clockTick: () => playTone([
    { freq: 1760, start: 0, dur: 0.035, type: "square", gain: 0.035 },
  ]),
  // Online waiting room (components/room/OnlineWaitingRoom.jsx): someone sits down / the table is full.
  seat: () => playTone([
    { freq: 587, start: 0,    dur: 0.12, type: "sine", gain: 0.12 },
    { freq: 880, start: 0.09, dur: 0.24, type: "sine", gain: 0.12 },
  ]),
  tableFull: () => playTone([
    { freq: 587, start: 0,    dur: 0.12, type: "triangle", gain: 0.14 },
    { freq: 740, start: 0.10, dur: 0.12, type: "triangle", gain: 0.14 },
    { freq: 880, start: 0.20, dur: 0.30, type: "triangle", gain: 0.15 },
  ]),
  victory: () => playTone([
    { freq: 523, start: 0,    dur: 0.14, type: "triangle", gain: 0.16 },
    { freq: 659, start: 0.14, dur: 0.14, type: "triangle", gain: 0.16 },
    { freq: 784, start: 0.28, dur: 0.18, type: "triangle", gain: 0.16 },
    { freq: 1046, start: 0.46, dur: 0.32, type: "triangle", gain: 0.18 },
  ]),
  defeat: () => playTone([
    { freq: 392, start: 0,    dur: 0.18, type: "triangle", gain: 0.14 },
    { freq: 311, start: 0.18, dur: 0.18, type: "triangle", gain: 0.14 },
    { freq: 247, start: 0.36, dur: 0.34, type: "triangle", gain: 0.14 },
  ]),
};

// ---------- Local Notifications ----------
let _notifReady = false;
let _notifGranted = false;

export const ensureNotificationPermission = async () => {
  if (_notifReady) return _notifGranted;
  _notifReady = true;
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status.display === "granted") {
      _notifGranted = true;
      return true;
    }
    const req = await LocalNotifications.requestPermissions();
    _notifGranted = req.display === "granted";
    return _notifGranted;
  } catch {
    _notifGranted = false;
    return false;
  }
};

let _lastNotifAt = 0;
export const notifyYourTurn = async (roundNumber = 1) => {
  // Only notify if the page is hidden (don't spam when user is actively looking)
  if (typeof document !== "undefined" && document.visibilityState === "visible") return;
  if (!_notifGranted) return;
  const now = Date.now();
  if (now - _lastNotifAt < 2500) return; // throttle
  _lastNotifAt = now;
  try {
    await LocalNotifications.schedule({
      notifications: [{
        id: Math.floor(Math.random() * 1e9),
        title: t("common.brand"),
        body: t("notif.yourTurn", { n: roundNumber }),
        schedule: { at: new Date(Date.now() + 200) },
      }],
    });
  } catch {}
};
